import { useState } from 'react';
import { AccordionRubric, Text } from 'shell/components';

const criteria = [
    {
        title: 'Thesis Statement',
        description: 'The clarity and presence of an argument that the essay revolves around.',
        options: [
            {
                title: 'Outstanding',
                description: 'Thesis is profound, expertly presented, and perfectly supported by the essay.',
                rate: '10',
                id: '3832bd92-f284-40d8-934c-92faaae5882b',
                suggested: true,
            },
            {
                title: 'Excellent',
                description:
                    'Thesis is exceptionally insightful, specific, and convincingly supported throughout the essay.',
                rate: '8',
                id: '1aa80ffa-75f4-4a3f-91d4-816b583163a7',
            },
            {
                title: 'Very Good',
                description: 'Thesis is compelling, sophisticated, and thoroughly supported by the essay.',
                rate: '7',
                id: '5a69eccc-ddf9-45ed-b8c5-475aa6851637',
            },
            {
                title: 'Good',
                description: 'Thesis is insightful, specific, and well supported by the essay.',
                rate: '6',
                id: '48b979e7-158c-48cb-932a-d08935ff7357',
            },
            {
                title: 'Competent',
                description: 'Thesis is clear, specific, and begins to show depth.',
                rate: '5',
                id: '28d5bd19-5365-4b6b-9890-7e83b39c89ff',
            },
            {
                title: 'Developing',
                description: 'Thesis is clear and specific but lacks depth or sophistication.',
                rate: '4',
                id: 'db29a50d-3134-4e9f-9312-9374243025ab',
            },
            {
                title: 'Adequate',
                description: 'Thesis is clear but could be more specific or compelling.',
                rate: '3',
                id: 'db37677b-eab4-40cf-a829-90d0aba3b575',
            },
            {
                title: 'Fair',
                description: 'Thesis is identifiable but is too broad or not directly supported by the essay.',
                rate: '2',
                id: '6301dd1f-e33e-46bb-9414-ed35e377d020',
            },
            {
                title: 'Poor',
                description: 'Thesis is present but lacks clarity or relevance to the topic.',
                rate: '1',
                id: 'f49b2e92-025f-4099-b480-ea0697e2d78b',
            },
            {
                title: 'Lacking',
                description: 'No discernible thesis or central argument.',
                rate: '0',
                id: 'b77b2d8b-ee5d-4aa5-a325-b291900e57af',
            },
        ],
        id: '428f717c-8396-4cac-91a3-fa60319cf158',
    },
    {
        title: 'Organization',
        description: 'The overall structure and flow of the essay.',
        options: [
            {
                title: 'Outstanding',
                description:
                    'Exemplary organization. The essay is compellingly structured for maximum impact and readability.',
                rate: '10',
                id: 'bbb27bf1-3eb0-4103-8ef2-0afee4500cf1',
            },
            {
                title: 'Excellent',
                description: 'Excellent organization. The structure enhances the argument and engages the reader.',
                rate: '8',
                id: 'f9d84a66-df67-443f-bf96-9f10ebda820b',
                suggested: true,
            },
            {
                title: 'Very Good',
                description: 'Very strong organization. The essay flows logically and effortlessly.',
                rate: '7',
                id: '88ec33b2-d079-462c-90ad-3b570cffa678',
            },
            {
                title: 'Good',
                description: 'Strong organization. Transitions are smooth and enhance the flow of the essay.',
                rate: '6',
                id: '950434f1-1d02-4b43-b1c4-84964b095976',
            },
            {
                title: 'Competent',
                description: 'Good organization and structure. Transitions are generally smooth.',
                rate: '5',
                id: '12d658f9-d6be-4c8a-a8d9-2cec7c627a0c',
            },
            {
                title: 'Developing',
                description: 'Clear organization. Most transitions work well.',
                rate: '4',
                id: 'd569ca27-5b15-4d44-be50-934a786e5fb7',
            },
            {
                title: 'Adequate',
                description: 'Basic organization is evident, but transitions could be smoother.',
                rate: '3',
                id: '7e243943-5616-4d73-b8e5-ace918d362ea',
            },
            {
                title: 'Fair',
                description: 'Some attempt at organization, but the essay lacks clear progression of ideas.',
                rate: '2',
                id: '954c19b2-6686-4440-8a13-2680ec04c005',
            },
            {
                title: 'Poor',
                description:
                    'Minimal organization. Transitions are awkward or missing, making the essay difficult to follow.',
                rate: '1',
                id: 'b534e9b8-21fb-425a-a426-4100f406e171',
            },
            {
                title: 'Lacking',
                description: 'No apparent organization. Ideas are disjointed and hard to follow.',
                rate: '0',
                id: 'e4819e86-1733-4e3d-a195-4a85c12a5978',
            },
        ],
        id: '61794326-cb05-43ee-ab3d-8f3252e6cf48',
    },
    {
        title: 'Support and Evidence',
        description: 'The use and quality of evidence and examples to support the thesis.',
        options: [
            {
                title: 'Outstanding',
                description:
                    'Exceptional, insightful use of high-quality evidence. Elevates the thesis to a profound level of support.',
                rate: '10',
                id: '941b482a-fad8-46f2-a22b-dbe9177b5ec2',
            },
            {
                title: 'Excellent',
                description:
                    'Excellent integration of highly compelling evidence. Supports thesis with depth and insight.',
                rate: '8',
                id: '72e3fdb8-3ae4-4c08-ba1f-4914dccd8be7',
            },
            {
                title: 'Very Good',
                description: 'Very strong, detailed evidence. Use of evidence is persuasive and sophisticated.',
                rate: '7',
                id: 'b43a69f6-7d21-4343-9361-c3f96ef6d299',
                suggested: true,
            },
            {
                title: 'Good',
                description: 'Strong and persuasive use of evidence to support thesis throughout.',
                rate: '6',
                id: '6d47374a-9cdf-4105-b7d1-b1c06e7aaccb',
            },
            {
                title: 'Competent',
                description: 'Competent use of relevant and effective evidence to support thesis.',
                rate: '5',
                id: 'd06ba72e-b1a7-4924-b482-901eaf26cd29',
            },
            {
                title: 'Developing',
                description: 'Good use of evidence to support most points, but some evidence may be less compelling.',
                rate: '4',
                id: '9bc9f8f3-a03e-47c5-a367-9d5ceeec2f5e',
            },
            {
                title: 'Adequate',
                description: 'Adequate support for the thesis but not fully developed or compelling.',
                rate: '3',
                id: '9db90777-34d6-44c2-93c6-d1e21e1b3a19',
            },
            {
                title: 'Fair',
                description: 'Some evidence present but is either too general, scarce, or of questionable relevance.',
                rate: '2',
                id: '2b5c6f29-c101-42b2-b96f-335b0a7d264b',
            },
            {
                title: 'Poor',
                description: 'Minimal or irrelevant support for the thesis.',
                rate: '1',
                id: 'b61dbcb3-2274-41b7-b692-edcd71c4763f',
            },
            {
                title: 'Lacking',
                description: 'No relevant support or evidence for thesis.',
                rate: '0',
                id: '49ce33f8-6bc8-4442-8dd0-8a7cadddf60f',
            },
        ],
        id: 'c6b0532e-443f-4e0d-8541-78ede0c46bfc',
    },
    {
        title: 'Writing Style and Tone',
        description: 'The appropriateness of style, tone, and voice for the intended audience and purpose.',
        options: [
            {
                title: 'Outstanding',
                description:
                    'Outstanding and masterful use of style and tone. Elevates the content and engages the reader on all levels.',
                rate: '10',
                id: '6cea2455-c9df-4389-af3e-0f9741ba41d9',
            },
            {
                title: 'Excellent',
                description:
                    'Excellent and compelling style and tone. Perfectly suits the essay’s audience and purpose.',
                rate: '8',
                id: '4cb31ffd-d152-46ad-8a59-7b0402b3e3c1',
            },
            {
                title: 'Very Good',
                description: 'Very effective style and tone. Engages the audience and enhances the essay’s purpose.',
                rate: '7',
                id: '9462dec0-7d29-4c55-b285-7732af8cae25',
            },
            {
                title: 'Good',
                description: 'Strong, clear, and consistent style and tone that suits the audience and purpose well.',
                rate: '6',
                id: '1a25e11e-cb7f-47ac-bad6-54152b17c138',
                suggested: true,
            },
            {
                title: 'Competent',
                description: 'Good match of style and tone to audience and purpose. Few inconsistencies.',
                rate: '5',
                id: '31efb36a-ca08-444b-be48-d364eb50cdec',
            },
            {
                title: 'Developing',
                description: 'Appropriate and consistent style and tone for the most part.',
                rate: '4',
                id: 'ba4ea423-a115-4e8b-b2bd-8cafecf40604',
            },
            {
                title: 'Adequate',
                description:
                    'Mostly appropriate style and tone, though some parts may not fully align with the audience or purpose.',
                rate: '3',
                id: '5272f06a-3f48-49a8-b4e9-726d02fc312c',
            },
            {
                title: 'Fair',
                description: 'Style and tone are not always appropriate or consistent.',
                rate: '2',
                id: 'd13cc0ef-e259-4799-94b1-5417ac37a7d5',
            },
            {
                title: 'Poor',
                description: 'Style and tone are somewhat inappropriate for the intended audience or purpose.',
                rate: '1',
                id: '5e0b3640-ff60-46dc-9c6b-2f4347e55996',
            },
            {
                title: 'Lacking',
                description: 'Inconsistent or inappropriate style and tone. Hard to read.',
                rate: '0',
                id: 'be2ac3fc-38dd-4964-a8f1-d508584f13be',
            },
        ],
        id: 'f3b6facc-9043-4196-b391-3dbca5e22a29',
    },
    {
        title: 'Grammar and Mechanics',
        description: 'The correctness of grammar, spelling, punctuation, and overall mechanics.',
        options: [
            {
                title: 'Outstanding',
                description:
                    'Exceptional command of grammar, spelling, and mechanics. Enhances the clarity and impact of the essay.',
                rate: '10',
                id: '29bf40cc-bc68-4b9f-8888-673ce5571837',
            },
            {
                title: 'Excellent',
                description: 'No errors in grammar, spelling, and mechanics. Perfectly polished and proofread.',
                rate: '8',
                id: '5cd39eda-70e6-43d9-8ab4-b9603cef41ee',
            },
            {
                title: 'Very Good',
                description: 'Almost no errors in grammar, spelling, and mechanics. Polished and well proofread.',
                rate: '7',
                id: '5ae426e7-60ac-4ad9-87ff-659bdccf0541',
            },
            {
                title: 'Good',
                description: 'Very few errors in grammar, spelling, and mechanics. They are barely noticeable.',
                rate: '6',
                id: 'a4a8b6f4-16ce-4789-bb95-efdae79f2c26',
            },
            {
                title: 'Competent',
                description: 'Occasional errors in grammar, spelling, and mechanics, though not significant.',
                rate: '5',
                id: 'dda36223-2a95-4ea1-bcdc-cbb715790e47',
                suggested: true,
            },
            {
                title: 'Developing',
                description: 'Minor errors in grammar, spelling, and mechanics present but are not distracting.',
                rate: '4',
                id: '7a7e273f-70bf-4775-87a0-49b02c1935c7',
            },
            {
                title: 'Adequate',
                description: 'Few errors in grammar, spelling, and mechanics. Mostly does not impede readability.',
                rate: '3',
                id: '524098cb-645b-4d90-bd0b-cd325c58cf65',
            },
            {
                title: 'Fair',
                description:
                    'Some errors in grammar, spelling, and mechanics, but does not greatly detract from readability.',
                rate: '2',
                id: '06d224c9-77fa-41eb-aa9e-c5c08ad58c18',
            },
            {
                title: 'Poor',
                description: 'Many errors in grammar, spelling, and mechanics that impact readability.',
                rate: '1',
                id: '37a4d5da-08c9-4ee1-b025-550bc43c381c',
            },
            {
                title: 'Lacking',
                description:
                    'Frequent and consistent errors in grammar, spelling, and mechanics that severely hamper readability.',
                rate: '0',
                id: '71f5d82d-70cc-4765-ab92-8dc893e2a749',
            },
        ],
        id: '30b9f1a3-26bc-49fb-99db-e6dc468f5883',
    },
];

const AccordionRubricDemo = () => {
    const [form, setForm] = useState({});
    const [selectedCriterionId, setSelectedCriterionId] = useState('428f717c-8396-4cac-91a3-fa60319cf158');

    return (
        <>
            <Text variant={'sh1'}>AccordionRubric</Text>
            <AccordionRubric
                criteria={criteria}
                disabled={false}
                form={form}
                selectedCriterionId={selectedCriterionId}
                setSelectedCriterionId={setSelectedCriterionId}
                onChange={(criterionId, optionId) => setForm((current) => ({ ...current, [criterionId]: optionId }))}
            />
        </>
    );
};

export default AccordionRubricDemo;
